import React from 'react';
import { ArrowLeftRight, Download, Upload, TrendingUp } from 'lucide-react';
interface AccountDetailsPanelProps {
  accountType: 'main' | 'futures' | 'inverse-futures' | 'earn';
}
export function AccountDetailsPanel({
  accountType
}: AccountDetailsPanelProps) {
  // Mock data based on account type
  const getAccountDetails = () => {
    switch (accountType) {
      case 'main':
        return {
          balance: 4.79,
          health: 98.78,
          marginHealth: 98.78,
          leverage: 10.14,
          collateralValue: 4.79,
          upnl: 0.08756,
          marginEquity: 4.88,
          usedMargin: 4.94,
          availMargin: -0.05973,
          maintenanceMargin: 3.95
        };
      case 'futures':
        return {
          balance: 0.0,
          health: null,
          marginHealth: null,
          leverage: null,
          collateralValue: 0.0,
          upnl: 0.0,
          marginEquity: 0.0,
          usedMargin: 0.0,
          availMargin: 0.0,
          maintenanceMargin: 0.0
        };
      case 'inverse-futures':
        return {
          balance: 0.0,
          health: null,
          marginHealth: null,
          leverage: null,
          collateralValue: 0.0,
          upnl: 0.0,
          marginEquity: 0.0,
          usedMargin: 0.0,
          availMargin: 0.0,
          maintenanceMargin: 0.0
        };
      case 'earn':
        return {
          balance: 3247.89,
          totalRewards: 42.15,
          weeklyRewards: 42.15
        };
      default:
        return null;
    }
  };
  const details = getAccountDetails();
  if (!details) return null;
  const getHealthColor = (health: number | null) => {
    if (health === null) return 'text-text-secondary';
    if (health > 80) return 'text-success';
    if (health > 50) return 'text-warning';
    return 'text-danger';
  };
  return <div className="glass-border rounded-[20px] p-6 space-y-6" data-id="element-1382">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-border-subtle" data-id="element-1383">
        <h3 className="text-lg font-semibold text-text-primary" data-id="element-1384">
          {accountType === 'main' && 'Main wallet details'}
          {accountType === 'futures' && 'Futures wallet details'}
          {accountType === 'inverse-futures' && 'Inverse Futures wallet details'}
          {accountType === 'earn' && 'Earn wallet details'}
        </h3>
      </div>

      {/* Account Details */}
      <div className="space-y-4" data-id="element-1385">
        {accountType !== 'earn' ? <>
            <div className="flex items-center justify-between" data-id="element-1386">
              <span className="text-sm text-text-secondary" data-id="element-1387">Balance</span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1388">
                {details.balance.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1389">CHF</span>
              </span>
            </div>

            {details.health !== null && <div className="flex items-center justify-between" data-id="element-1390">
                <span className="text-sm text-text-secondary" data-id="element-1391">Health</span>
                <span className={`text-sm font-numbers font-medium ${getHealthColor(details.health)}`} data-id="element-1392">
                  {details.health > 0 ? details.health.toFixed(2) + '%' : '-'}
                </span>
              </div>}

            {details.marginHealth !== null && <div className="flex items-center justify-between" data-id="element-1393">
                <span className="text-sm text-text-secondary" data-id="element-1394">
                  Margin health
                </span>
                <span className={`text-sm font-numbers font-medium ${getHealthColor(details.marginHealth)}`} data-id="element-1395">
                  {details.marginHealth > 0 ? details.marginHealth.toFixed(2) + '%' : '-'}
                </span>
              </div>}

            {details.leverage !== null && <div className="flex items-center justify-between" data-id="element-1396">
                <span className="text-sm text-text-secondary" data-id="element-1397">Leverage</span>
                <span className="text-sm font-numbers text-text-primary" data-id="element-1398">
                  {details.leverage > 0 ? details.leverage.toFixed(2) + 'x' : '-'}
                </span>
              </div>}

            <div className="h-px bg-border-subtle my-4" data-id="element-1399" />

            <div className="flex items-center justify-between" data-id="element-1400">
              <span className="text-sm text-text-secondary" data-id="element-1401">
                Collateral value
              </span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1402">
                {details.collateralValue.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1403">CHF</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1404">
              <span className="text-sm text-text-secondary" data-id="element-1405">UP&L</span>
              <span className={`text-sm font-numbers ${details.upnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1406">
                {details.upnl >= 0 ? '+' : ''}
                {details.upnl.toFixed(5)}{' '}
                <span className="text-text-secondary" data-id="element-1407">CHF</span>
              </span>
            </div>

            <div className="h-px bg-border-subtle my-4" data-id="element-1408" />

            <div className="flex items-center justify-between" data-id="element-1409">
              <span className="text-sm text-text-secondary" data-id="element-1410">Margin equity</span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1411">
                {details.marginEquity.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1412">CHF</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1413">
              <span className="text-sm text-text-secondary" data-id="element-1414">Used margin</span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1415">
                {details.usedMargin.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1416">CHF</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1417">
              <span className="text-sm text-text-secondary" data-id="element-1418">Avail. margin</span>
              <span className={`text-sm font-numbers ${details.availMargin >= 0 ? 'text-text-primary' : 'text-danger'}`} data-id="element-1419">
                {details.availMargin.toFixed(5)}{' '}
                <span className="text-text-secondary" data-id="element-1420">CHF</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1421">
              <span className="text-sm text-text-secondary" data-id="element-1422">
                Maintenance margin
              </span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1423">
                {details.maintenanceMargin.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1424">CHF</span>
              </span>
            </div>
          </> : <>
            <div className="flex items-center justify-between" data-id="element-1425">
              <span className="text-sm text-text-secondary" data-id="element-1426">Balance</span>
              <span className="text-sm font-numbers text-text-primary" data-id="element-1427">
                {details.balance.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}{' '}
                <span className="text-text-secondary" data-id="element-1428">USD</span>
              </span>
            </div>

            <div className="flex items-center justify-between" data-id="element-1429">
              <span className="text-sm text-text-secondary" data-id="element-1430">Total rewards</span>
              <span className="text-sm font-numbers text-success" data-id="element-1431">
                +{details.totalRewards.toFixed(2)}{' '}
                <span className="text-text-secondary" data-id="element-1432">USD</span>
              </span>
            </div>

            <div className="h-px bg-border-subtle my-4" data-id="element-1433" />

            <div className="bg-background-elevated rounded-lg p-4" data-id="element-1434">
              <div className="flex items-center gap-2 mb-2" data-id="element-1435">
                <TrendingUp size={16} className="text-success" data-id="element-1436" />
                <span className="text-xs text-text-secondary" data-id="element-1437">
                  Weekly Rewards
                </span>
              </div>
              <div className="text-lg font-numbers text-success" data-id="element-1438">
                +{details.weeklyRewards.toFixed(2)}{' '}
                <span className="text-sm text-text-secondary" data-id="element-1439">USD</span>
              </div>
            </div>
          </>}
      </div>

      {/* Action Buttons */}
      <div className="space-y-2 pt-4 border-t border-border-subtle" data-id="element-1440">
        <button className="w-full min-h-[44px] flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border-subtle hover:border-border-default rounded-lg transition-colors" data-id="element-1441">
          <ArrowLeftRight size={16} data-id="element-1442" />
          Transfer
        </button>
        <button className="w-full min-h-[44px] flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 border border-border-subtle hover:border-border-default rounded-lg transition-colors" data-id="element-1443">
          <Upload size={16} data-id="element-1444" />
          Withdraw
        </button>
        <button className="w-full min-h-[44px] flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-brand-purple/20 backdrop-blur-sm hover:bg-brand-purple/30 text-white rounded-xl transition-colors border border-[rgba(122,46,255,0.25)] border-t-[rgba(122,46,255,0.5)] hover:border-[rgba(122,46,255,0.35)] hover:border-t-[rgba(122,46,255,0.6)]" data-id="element-1445">
          <Download size={16} data-id="element-1446" />
          Deposit
        </button>
      </div>
    </div>;
}